import React, { useMemo } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import { useAPIProvider } from '../../../../common/hooks/use-api-provider';
import { useAnnotationTimelineHandlers } from '../../../labelling/components/frames-control-panel/frames-timeline/annotation-timeline-v2/hooks/use-annotation-timeline-handlers';
import type { BoundingBoxTrackingInputT } from '../../../types';
import { dataUrlAtom } from '../../atoms/file-atoms';
import { timelineRowsWithPreviewsAtom, timelineZoomAtom } from '../../atoms/frame-timeline-v4';
import { dispatchAtom } from '../../atoms/label-store-dispatch/dispatch';
import { EVENTS, UNDOABLE_EVENTS } from '../../atoms/label-store-dispatch/events';
import { timelineExpandedAnnotationIdsAtom } from '../../atoms/timeline-expansion-atoms';
import { CELL_WIDTH } from '../../constants/frame-timeline-v4';
import type { TimelineActionsT } from '../../types/frame-timeline-v4';
import { showNotificationViaToaster } from '../../utils/notification-utils';
import { FrameTimeline } from './frame-timeline';
type PropsT = {
  totalFrames: number;
};
export const FrameTimelineContainer = ({
  totalFrames
}: PropsT) => {
  const rows = useAtomValue(timelineRowsWithPreviewsAtom);
  const zoom = useAtomValue(timelineZoomAtom);
  const toggleTimelineExpanded = useSetAtom(timelineExpandedAnnotationIdsAtom);
  const dispatch = useSetAtom(dispatchAtom);
  const {
    getAnnotationTrackingData,
    getBoundingBoxData
  } = useAPIProvider();
  const dataUrl = useAtomValue(dataUrlAtom);
  const getBoundingBoxTrackingData = useMemo(() => {
    if (!getBoundingBoxData) return undefined;
    return (frameIndex: number, bbox: BoundingBoxTrackingInputT['bbox'], numFramesToTrack: number) => getBoundingBoxData({
      videoUrl: dataUrl ?? '',
      initialFrame: frameIndex,
      bbox,
      numFramesToTrack
    });
  }, [getBoundingBoxData, dataUrl]);
  const {
    handleOnChange
  } = useAnnotationTimelineHandlers({
    showNotification: showNotificationViaToaster,
    fetchAnnotationTrackingData: getAnnotationTrackingData,
    getBoundingBoxTrackingData
  });
  const cellWidth = CELL_WIDTH * zoom / 100;
  const timelineActions = useMemo<TimelineActionsT>(() => ({
    onToggleRowExpand: rowId => toggleTimelineExpanded(rowId),
    onSelectRow: rowId => dispatch({
      type: EVENTS.SELECT_ANNOTATION,
      annotationId: rowId
    }),
    onNavigateToFrame: frameIndex => dispatch({
      type: EVENTS.SET_CURRENT_FRAME,
      frameId: frameIndex
    }),
    onDeleteAnnotationSpan: params => dispatch({
      type: UNDOABLE_EVENTS.DELETE_ANNOTATION_FRAME_SPAN,
      ...params
    }),
    onUpdateAnnotationAttribute: params => dispatch({
      type: UNDOABLE_EVENTS.UPDATE_ANNOTATION_ATTRIBUTE,
      ...params
    }),
    onDeleteAnnotationAttribute: params => dispatch({
      type: UNDOABLE_EVENTS.DELETE_ANNOTATION_ATTRIBUTE,
      ...params
    }),
    onTrackAnnotation: ({
      annotationId,
      annotationType,
      annotationSequence
    }) => handleOnChange(annotationId, annotationType, annotationSequence, () => {}, true)
  }), [dispatch, toggleTimelineExpanded, handleOnChange]);
  return <FrameTimeline totalFrames={totalFrames} rows={rows} timelineActions={timelineActions} cellWidth={cellWidth} />;
};